/* eslint-disable */
import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Navigate,
	Route,
	RouterProvider,
	createBrowserRouter,
	Routes,
} from "react-router-dom";
import "./App.css";
import Providers from "./utils/provider";
import "./i18n/config";
import { useAuth0 } from "@auth0/auth0-react";
import Spinner from "./components/Spinner";
import Layout from "./views/Layout";
import UserList from "./pages/User/List";
import AddUser from "./pages/User/AddUser";
import EditUser from "./pages/User/EditUser";
import { useUserStore } from "./stores/user.store";
import { useMenuStore } from "./stores/menu.store";
import HelpSupportList from "./pages/HelpAndSupport/List";
import Unauthorized from "./pages/Unauthorized/Index";
import { resolveReturnTo } from "./utils/returnTo";
import AccessDenied from "./pages/AccessDenied";
import BeneficiaryReport from "./pages/Reports/BeneficiaryReport";
import ConsolidatedReport from "./pages/Reports/ConsolidatedReport";
import ReportsDashboard from "./pages/Reports/ReportsDashboard";
import CardsList from "./pages/Cards";
import TreatmentPlans from "./pages/TreatmentPlans";
import Events from "./pages/Events";
import Actions from "./pages/Actions";
import Roles from "./pages/Roles";
import GroupLabels from "./pages/GroupLabels";
import RejectionReasons from "./pages/RejectionReasons";
import Organizations from "./pages/Organizations";
import Accounts from "./pages/Accounts";
import Programs from "./pages/Programs";
import ReviewQueuePage from "./pages/ReviewQueue";
import { UserStatus } from "./domain/user.interface";
import Callback from "./pages/Callback";
import Dashboard from "./pages/Dashboard";
import DashboardB2D from "./pages/DashboardB2D";
import ProviderReport from "./pages/Reports/ProviderReport";
import {
	setToken,
	setTokenBo,
	setup401Interceptor,
	setInitialAuthFlag,
} from "./utils/axios.create";
import RewardsConfigPage from "./pages/RewardsConfiguration";
import EmailPrograms from "./pages/EmailPrograms";
import SettingsManagement from "./pages/SettingsManagement";
import Payers from "./pages/Payers";
// SIMS-8783 — /cups now serves the merged Cups & Analytes screen; the standalone Cups screen it
// replaces is gone from the router. The Analytes screen is still mounted, but INSIDE the merged
// screen's Analytes tab rather than on a route of its own.
import CupsAndAnalytes from "./pages/CupsAndAnalytes";
import {
	CUPS_ANALYTES_PATH,
	LEGACY_ANALYTES_PATH,
	analytesRedirectTarget,
} from "./pages/CupsAndAnalytes/tabs";
import {
	centralizedLogout,
	isUserDataStale,
	isTokenRefreshError,
	classifyError,
	AUTH_CONSTANTS,
} from "./utils/auth";
import { useProactiveTokenRefresh } from "./hooks/useProactiveTokenRefresh";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { AuthErrorScreen, NetworkErrorScreen } from "./components/ErrorScreens";
import { useThemeStore } from "./stores/theme.store";
import { useBeneficiaryTermStore } from "./stores/beneficiaryTerm.store";
import { settingService } from "./services/setting.service";

const BeneficiaryList = React.lazy(() => import("./pages/Beneficiary/List"));
const BeneficiaryProfile = React.lazy(
	() => import("./pages/Beneficiary/Profile"),
);
const ParticipantList = React.lazy(() => import("./pages/Participants/List"));
const ParticipantProfile = React.lazy(
	() => import("./pages/Participants/Profile"),
);

function AppContent() {
	const [userFetchError, setUserFetchError] = useState(false);
	const [networkError, setNetworkError] = useState(false);
	const [loginAttempts, setLoginAttempts] = useState(0);
	const [auth0InitComplete, setAuth0InitComplete] = useState(false);
	const [settingsReady, setSettingsReady] = useState(false);
	const tokenSetRef = useRef(false);
	const initializingRef = useRef(false);
	const redirectingRef = useRef(false);

	const {
		loginWithRedirect,
		logout,
		isAuthenticated,
		isLoading: isAuthLoading,
		error,
		user,
		getAccessTokenSilently,
	} = useAuth0();

	const {
		menus,
		userInfo,
		lastFetchedAt,
		isLoading: isUserLoading,
		getUserByEmail,
		clearUser,
	} = useUserStore();

	const { setMenuItems } = useMenuStore();
	const { setThemeColors } = useThemeStore();
	const { setTerms: setBeneficiaryTerms } = useBeneficiaryTermStore();

	// Centralized logout handler
	const handleLogout = useCallback(
		async (reason: string) => {
			console.log(`[App] Logging out: ${reason}`);
			await centralizedLogout(clearUser, logout, reason as any);
		},
		[clearUser, logout],
	);

	// Session expired handler for 401 interceptor
	const handleSessionExpired = useCallback(() => {
		handleLogout("session_expired");
	}, [handleLogout]);

	// Set up 401 interceptor once with token refresh + session expiry handler
	useEffect(() => {
		setup401Interceptor(getAccessTokenSilently, handleSessionExpired);
	}, [getAccessTokenSilently, handleSessionExpired]);

	// Track when Auth0 initialization is complete
	useEffect(() => {
		if (!isAuthLoading) {
			console.log("[App] Auth0 initialization complete");
			setAuth0InitComplete(true);
		}
	}, [isAuthLoading]);

	useEffect(() => {
		if (userInfo && menus) {
			setMenuItems(menus);
		}
	}, [userInfo, menus, setMenuItems]);

	// Settings loading moved into the main auth flow (initializeUser)
	// to ensure it runs after tokens are set and validates the token.

	// Main authentication flow with improved error handling
	useEffect(() => {
		const email = user?.email;
		const sub = user?.sub;

		if (!isAuthLoading && isAuthenticated && email && sub) {
			const initializeUser = async () => {
				// Prevent duplicate initialization
				if (initializingRef.current) {
					console.log("[App] Initialization already in progress, skipping...");
					return;
				}

				try {
					initializingRef.current = true;
					console.log("[App] Initializing user authentication...");

					// Set flag to prevent 401 interceptor during initial auth
					setInitialAuthFlag(true);

					// Get access token. Force a fresh token from Auth0 instead of
					// reading the cached one from localStorage, which may already
					// be expired on a returning session and trigger a 401 on the
					// first /user-info call before the SDK silently refreshes.
					const token = await getAccessTokenSilently({ cacheMode: "off" });

					// Set tokens for axios instances
					setToken(token);
					setTokenBo(token);
					tokenSetRef.current = true;

					console.log("[App] Tokens set successfully");

					// Check if user data is stale or missing
					const needsRefresh =
						!userInfo ||
						isUserDataStale(lastFetchedAt, AUTH_CONSTANTS.USER_DATA_STALE_TIME);

					// Fire user fetch (Backend1) and settings fetch (Backend2) in
					// parallel — they hit different backends and the rest of the
					// initialisation only needs both to be done before unblocking
					// the UI. Doing them sequentially used to add the latency of
					// the slower backend on top of the faster one on every login.
					const fetchUserWithRetry = async (): Promise<void> => {
						if (!needsRefresh) {
							console.log("[App] Using cached user data");
							return;
						}
						console.log("[App] Fetching user data from database...");

						// Retry logic for initial user fetch (handles timing issues with token propagation)
						let retryCount = 0;
						const maxRetries = 2;
						let lastError: any;

						while (retryCount <= maxRetries) {
							try {
								await getUserByEmail(email, sub);
								console.log("[App] User data fetched successfully");
								return;
							} catch (error: any) {
								lastError = error;
								retryCount++;

								if (
									error.response?.status === 401 &&
									retryCount <= maxRetries
								) {
									const delay = 500 * retryCount; // 500ms, 1000ms
									console.log(
										`[App] Initial auth 401 error, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`,
									);
									await new Promise((resolve) => setTimeout(resolve, delay));
								} else {
									throw error;
								}
							}
						}

						if (lastError) throw lastError;
					};

					const applySettings = (settings: unknown): void => {
						const s = settings as Record<string, unknown>;
						const themeColors = s.themeColors as
							| {
									primaryColor: string;
									secondaryColor: string;
									surfaceColor?: string;
									surfaceLight?: string;
									surfaceDark?: string;
									primaryDark?: string;
									secondaryLight?: string;
									outlineMain?: string;
									outlineDark?: string;
									textMainDark2?: string;
									textMainMain2?: string;
									textSecondaryMain?: string;
									tertiaryColor?: string;
									surfaceTint?: string;
									fontFamily?: string;
							  }
							| undefined;
						if (
							themeColors &&
							themeColors.primaryColor &&
							themeColors.secondaryColor
						) {
							setThemeColors(themeColors);
						}
						const beneficiaryTerms = s.beneficiaryTerms as
							| { singular: string; plural: string }
							| undefined;
						if (
							beneficiaryTerms &&
							beneficiaryTerms.singular &&
							beneficiaryTerms.plural
						) {
							setBeneficiaryTerms(beneficiaryTerms);
						}
					};

					const loadSettings = async (): Promise<void> => {
						// Mirror the 401-retry pattern from fetchUserWithRetry —
						// the Backend2 authorizer Lambda can return a transient
						// 401 from API Gateway on its first cold-start invocation
						// (the authorizer integration tops out before the JWKS
						// fetch completes). A short back-off retry rides over
						// that without surfacing the error to the user.
						let retryCount = 0;
						const maxRetries = 2;
						while (retryCount <= maxRetries) {
							try {
								const settings = await settingService.getSettings();
								applySettings(settings);
								return;
							} catch (error: any) {
								if (
									error?.response?.status === 401 &&
									retryCount < maxRetries
								) {
									const delay = 500 * (retryCount + 1); // 500ms, 1000ms
									await new Promise((r) => setTimeout(r, delay));
									retryCount++;
								} else {
									// Settings remain best-effort — failing here must
									// not block the rest of the login bootstrap.
									return;
								}
							}
						}
					};

					await Promise.all([fetchUserWithRetry(), loadSettings()]);
					setSettingsReady(true);

					// Clear error states
					setUserFetchError(false);
					setNetworkError(false);
				} catch (e: any) {
					console.error("[App] Authentication initialization error:", e);

					// Handle token refresh errors
					if (isTokenRefreshError(e)) {
						console.log("[App] Token refresh error, logging out");
						handleSessionExpired();
						return;
					}

					// Classify and handle other errors
					const errorType = classifyError(e);

					switch (errorType) {
						case "unauthorized":
							console.log(
								"[App] Unauthorized - token invalid or expired, logging out",
							);
							handleSessionExpired();
							break;

						case "access_denied":
							console.log("[App] Access denied - user not found or inactive");
							setUserFetchError(true);
							setNetworkError(false);
							break;

						case "network_error":
						case "server_error":
							console.log("[App] Network/server error during user fetch");
							setNetworkError(true);
							setUserFetchError(false);
							break;

						default:
							console.error("[App] Unknown error type");
							setNetworkError(true);
							setUserFetchError(false);
					}
				} finally {
					// Always clear the initial auth flag after initialization attempt
					setInitialAuthFlag(false);
					initializingRef.current = false;
				}
			};

			initializeUser();
		}
	}, [
		isAuthLoading,
		isAuthenticated,
		user?.email,
		user?.sub,
		getAccessTokenSilently,
		getUserByEmail,
		handleSessionExpired,
		userInfo,
		lastFetchedAt,
		auth0InitComplete,
	]);

	// Auto-redirect to login with infinite loop prevention
	// Only redirect after Auth0 has completed initialization
	useEffect(() => {
		// Don't redirect if we're on the callback page - let Auth0 handle it
		const isOnCallbackPage = window.location.pathname === "/callback";

		if (
			!isAuthenticated &&
			!isAuthLoading &&
			auth0InitComplete &&
			!isOnCallbackPage &&
			!redirectingRef.current
		) {
			if (loginAttempts < AUTH_CONSTANTS.MAX_LOGIN_ATTEMPTS) {
				console.log(
					`[App] Not authenticated, redirecting to login (attempt ${loginAttempts + 1})`,
				);
				// Guard against the effect re-running while `loginWithRedirect`
				// is still in flight: setLoginAttempts triggers a re-render and
				// the dependency array re-fires this effect before the SDK has
				// navigated away, which previously dispatched 2–3 parallel
				// /authorize calls that aborted each other and tripped the
				// MAX_LOGIN_ATTEMPTS guard with no real login attempts.
				redirectingRef.current = true;
				clearUser();
				localStorage.removeItem(AUTH_CONSTANTS.STORE_KEY);
				setLoginAttempts((prev) => prev + 1);
				// SIMS-9057: carry where they were trying to go through sign-in.
				// `onRedirectCallback` in index.tsx already returns to `appState.returnTo`
				// — until now nothing ever set it, so every signed-out visit to a deep
				// link landed on the dashboard.
				loginWithRedirect({
					appState: { returnTo: resolveReturnTo(window.location) },
				});
			} else {
				console.error("[App] Max login attempts reached, showing error");
				// Only set network error after multiple failed attempts
				// This ensures we don't show error on first load
				setNetworkError(true);
			}
		} else if (isAuthenticated) {
			// Reset login attempts on successful auth
			redirectingRef.current = false;
			setLoginAttempts(0);
			// Clear network error on successful auth
			setNetworkError(false);
		}
	}, [
		isAuthenticated,
		isAuthLoading,
		auth0InitComplete,
		loginAttempts,
		clearUser,
		loginWithRedirect,
	]);

	// Enable proactive token refresh and session timeout
	useProactiveTokenRefresh(tokenSetRef.current);
	useSessionTimeout(30, tokenSetRef.current);

	const showComponent = useMemo<
		"spinner" | "accessDenied" | "networkError" | "continue"
	>(() => {
		// Show spinner while Auth0 is initializing or loading
		// This prevents showing error screens during initial auth check
		if (isAuthLoading || !auth0InitComplete) {
			return "spinner";
		}

		// After Auth0 finishes loading, check for errors
		// Only show network error if Auth0 init is complete AND user is authenticated
		if (
			networkError &&
			!isAuthLoading &&
			auth0InitComplete &&
			isAuthenticated
		) {
			return "networkError";
		} else if (userFetchError) {
			return "accessDenied";
		} else if (isUserLoading || !user || !userInfo || !settingsReady) {
			return "spinner";
		} else if (userInfo.status === UserStatus.INACTIVE) {
			return "accessDenied";
		} else {
			return "continue";
		}
	}, [
		isAuthLoading,
		auth0InitComplete,
		isAuthenticated,
		isUserLoading,
		user,
		userInfo,
		userFetchError,
		networkError,
		settingsReady,
	]);

	// Auth0 error handling - only show error for critical errors
	// Ignore recoverable errors like login_required, consent_required, etc.
	if (error) {
		const errorMessage = error.message?.toLowerCase() || "";
		const errorCode = (error as any).error || "";
		const isRecoverableError =
			errorMessage.includes("login_required") ||
			errorMessage.includes("consent_required") ||
			errorMessage.includes("interaction_required") ||
			errorCode === "login_required" ||
			errorCode === "consent_required" ||
			errorCode === "interaction_required";

		// For recoverable errors, show loading spinner instead of error
		// The login redirect will handle these cases
		if (isRecoverableError) {
			console.log(
				"[App] Recoverable Auth0 error, showing loading state:",
				error,
			);
			// Don't show error - let the auth flow continue
		} else {
			// Only show error screen for critical Auth0 errors
			console.error("[App] Critical Auth0 error:", error);
			return (
				<AuthErrorScreen error={error} onRetry={() => loginWithRedirect()} />
			);
		}
	}

	// Network error UI
	if (showComponent === "networkError") {
		return (
			<NetworkErrorScreen
				onRetry={() => window.location.reload()}
				maxAttempts={AUTH_CONSTANTS.MAX_LOGIN_ATTEMPTS}
				currentAttempt={loginAttempts}
			/>
		);
	}

	// Render content based on app state
	const renderContent = () => {
		if (showComponent === "spinner") {
			return (
				<div className="flex w-screen h-screen items-center justify-center">
					<Spinner />
				</div>
			);
		}

		// Note: networkError is handled by early return above, not here

		if (showComponent === "accessDenied") {
			return <AccessDenied />;
		}

		if (showComponent === "continue") {
			return (
				<Layout>
					<Suspense
						fallback={
							<div className="flex w-full h-full items-center justify-center">
								<Spinner />
							</div>
						}
					>
						<Routes>
							<Route path="/" element={<Dashboard />} />
							<Route path="/dashboard" element={<Dashboard />} />
							<Route path="/dashboard-b2d" element={<DashboardB2D />} />
							<Route path="/users" element={<UserList />} />
							<Route path="/user" element={<AddUser />} />
							<Route path="/users/:id" element={<EditUser />} />
							<Route path="/beneficiaries">
								<Route index element={<BeneficiaryList />} />
								<Route path=":beneficiaryId" element={<BeneficiaryProfile />} />
							</Route>
							<Route path="/participants">
								<Route index element={<ParticipantList />} />
								<Route path=":participantId" element={<ParticipantProfile />} />
							</Route>
							<Route path="/cards" element={<CardsList />} />
							<Route path="/treatmentPlans" element={<TreatmentPlans />} />
							<Route path="/events" element={<Events />} />
							<Route path="/actions" element={<Actions />} />
							<Route path="/roles" element={<Roles />} />
							<Route path="/group-labels" element={<GroupLabels />} />
							<Route path="/rejection-reasons" element={<RejectionReasons />} />
							<Route path="/settings" element={<SettingsManagement />} />
							<Route path={CUPS_ANALYTES_PATH} element={<CupsAndAnalytes />} />
							{/* SIMS-8783 AC1/C17945 — the former analytes route no longer serves a
							    standalone screen. It redirects onto the merged screen with the Analytes
							    tab selected, so existing links and bookmarks keep working. */}
							<Route
								path={LEGACY_ANALYTES_PATH}
								element={<Navigate to={analytesRedirectTarget()} replace />}
							/>
							<Route path="/organizations" element={<Organizations />} />
							<Route path="/accounts" element={<Accounts />} />
							<Route path="/programs" element={<Programs />} />
							<Route path="/review-queue" element={<ReviewQueuePage />} />
							<Route path="/payers" element={<Payers />} />
							<Route
								path="/beneficiaryReport"
								element={<BeneficiaryReport />}
							/>
							<Route path="/providerReport" element={<ProviderReport />} />
							<Route path="/reports" element={<ConsolidatedReport />} />
							<Route path="/reports/center" element={<ReportsDashboard />} />
							<Route
								path="/reports/center/:report"
								element={<ReportsDashboard />}
							/>
							<Route path="/help-and-support" element={<HelpSupportList />} />
							<Route path="/unauthorized" element={<Unauthorized />} />
							<Route
								path="/rewards-configuration"
								element={<RewardsConfigPage />}
							/>
							{/* SIMS-9056. Reachable only once an Action with a matching
							    link exists and is on the user's role — Layout bounces an
							    unlisted path to /unauthorized. */}
							<Route path="/email-programs" element={<EmailPrograms />} />
						</Routes>
					</Suspense>
				</Layout>
			);
		}

		return null;
	};

	return (
		<>
			<Providers>
				<Routes>
					{/* Callback route is always accessible, regardless of auth state */}
					<Route path="/callback" element={<Callback />} />

					{/* All other routes render based on authentication state */}
					<Route path="*" element={renderContent()} />
				</Routes>
			</Providers>
		</>
	);
}

/**
 * SIMS-9123 — the app runs under a DATA router, so an editor with unsaved changes can stop an
 * in-app navigation: react-router's `useBlocker` only works under one, and `<BrowserRouter>` is
 * not. The route tree is unchanged: one catch-all route renders the same `<Routes>` as before, so
 * every path, redirect and the /callback route resolve exactly as they did.
 *
 * Built once per mount rather than at import, so loading this module touches no history.
 */
function App() {
	const [router] = useState(() =>
		createBrowserRouter([{ path: "*", element: <AppContent /> }]),
	);
	return <RouterProvider router={router} />;
}

export default App;
